import moment from 'moment';
import 'moment/locale/pt-br';

moment.locale('pt-br');

/** Rótulo de localidade (município/UF) — usado quando várias cidades estão selecionadas. */
export const formatFeriadoLocalidade = (feriado) => {
  if (!feriado) return null;

  if (feriado.tipo === 'MUNICIPAL') {
    if (feriado.municipioNome && feriado.uf) {
      return `${feriado.municipioNome} / ${feriado.uf}`;
    }
    if (feriado.municipioNome) return feriado.municipioNome;
    if (feriado.codigoIbge) return String(feriado.codigoIbge);
  }

  if (feriado.tipo === 'ESTADUAL' && feriado.uf) {
    return feriado.uf;
  }

  return null;
};

export const formatFeriadoTooltip = (feriado, { showLocalidade = false } = {}) => {
  const localidade = showLocalidade ? formatFeriadoLocalidade(feriado) : null;
  if (localidade) return `${feriado.nome} — ${localidade}`;
  return feriado.nome;
};

/** Chave estável para agrupar feriados em "Próximos feriados" (data + nome + tipo). */
export const buildProximosFeriadoGroupKey = (feriado) => {
  const nome = (feriado.nome || '').trim().toLowerCase();
  return `${moment(feriado.data).format('YYYY-MM-DD')}|${nome}|${feriado.tipo}`;
};

/** Agrupa feriados com mesma data, nome e tipo — preserva ordem da primeira ocorrência. */
export const groupProximosFeriados = (feriados) => {
  const map = new Map();
  (feriados || []).forEach((f) => {
    const key = buildProximosFeriadoGroupKey(f);
    if (!map.has(key)) {
      map.set(key, { data: f.data, nome: f.nome, tipo: f.tipo, items: [] });
    }
    map.get(key).items.push(f);
  });
  return Array.from(map.values());
};

/** Localidades únicas de um grupo (ordem de aparição). */
export const getGroupedLocalidades = (items) => {
  const seen = new Set();
  const result = [];
  (items || []).forEach((f) => {
    const loc = formatFeriadoLocalidade(f);
    if (loc && !seen.has(loc)) {
      seen.add(loc);
      result.push(loc);
    }
  });
  return result;
};

/**
 * Monta texto resumido do subtítulo de localidades.
 * 4+ itens: 2 primeiros + "e mais N municípios"; tooltip sempre com lista completa.
 */
export const buildGroupedLocalidadesDisplay = (localidades) => {
  const all = localidades || [];
  if (!all.length) return null;

  const tooltipText = all.join(', ');
  if (all.length <= 3) {
    return { all, preview: null, overflowCount: 0, tooltipText };
  }

  return {
    all,
    preview: all.slice(0, 2).join(', '),
    overflowCount: all.length - 2,
    tooltipText,
  };
};

export const formatGroupedFeriadoTooltip = (group, { showLocalidade = false } = {}) => {
  if (!group?.items?.length) return '';
  if (group.items.length === 1) {
    return formatFeriadoTooltip(group.items[0], { showLocalidade });
  }
  if (!showLocalidade) return group.nome;
  const localidades = getGroupedLocalidades(group.items);
  if (!localidades.length) return group.nome;
  return `${group.nome} — ${localidades.join(', ')}`;
};

/**
 * Agrupa feriados do mesmo dia por nome + tipo (+ status).
 * Usado na célula/popover da CalendarGrid quando várias cidades compartilham o mesmo feriado.
 */
export const groupDayFeriados = (feriados) => {
  const map = new Map();
  (feriados || []).forEach((f) => {
    const nome = (f.nome || '').trim().toLowerCase();
    const key = `${nome}|${f.tipo}|${f.status || 'VALIDO'}`;
    if (!map.has(key)) {
      map.set(key, {
        id: f.id,
        nome: f.nome,
        tipo: f.tipo,
        status: f.status || 'VALIDO',
        items: [],
      });
    }
    map.get(key).items.push(f);
  });
  return Array.from(map.values());
};

/** Rótulo da tag na célula: "Nome" ou "Nome · N cidades". */
export const formatDayCellGroupLabel = (group) => {
  if (!group?.items?.length) return '';
  const count = group.items.length;
  if (count <= 1) return group.nome || '';
  const unit = count === 1 ? 'cidade' : 'cidades';
  return `${group.nome} · ${count} ${unit}`;
};

/** Filtra MUNICIPAL para códigos IBGE curados; NACIONAL/ESTADUAL passam. */
export const filterFeriadosByMunicipiosCurados = (feriados, codigoIbgeSet) => {
  if (!codigoIbgeSet || codigoIbgeSet.size === 0) return feriados || [];
  return (feriados || []).filter((f) => {
    if (f.tipo !== 'MUNICIPAL') return true;
    return f.codigoIbge != null && codigoIbgeSet.has(String(f.codigoIbge));
  });
};

/** Agrupa feriados por data (chave `YYYY-MM-DD`) — mesma lógica serve mês e semana. */
export const groupFeriadosByDate = (feriados) => {
  const map = new Map();
  (feriados || []).forEach((feriado) => {
    const key = moment(feriado.data).format('YYYY-MM-DD');
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(feriado);
  });
  return map;
};

/** Dias (moment) a renderizar na grade mensal — inclui padding de semanas anterior/posterior. */
export const buildMonthDays = (referenceDate) => {
  const start = moment(referenceDate).startOf('month').startOf('week');
  const end = moment(referenceDate).endOf('month').endOf('week');
  const days = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, 'day')) {
    days.push(cursor.clone());
    cursor.add(1, 'day');
  }
  return days;
};

/** Dias (moment) da semana corrente ao redor de `referenceDate`. */
export const buildWeekDays = (referenceDate) => {
  const start = moment(referenceDate).startOf('week');
  const days = [];
  for (let i = 0; i < 7; i += 1) {
    days.push(start.clone().add(i, 'day'));
  }
  return days;
};

/**
 * Paleta fixa por tipo de feriado (não segue theme.palette.*) — replica os tons do protótipo
 * (docs/calendario-feriados-v2-prototipo.html: --blue-main, --purple-main, --orange-main) para
 * um visual mais "clean" que as cores saturadas padrão do MUI (info/secondary/warning).
 */
export const TIPO_COLOR = {
  NACIONAL: { main: '#2970FF', light: '#F5F8FF', dark: '#004EEB' },
  ESTADUAL: { main: '#9E77ED', light: '#F9F5FF', dark: '#6941C6' },
  MUNICIPAL: { main: '#F97316', light: '#FFF7ED', dark: '#C2410C' },
  DAY_OFF_ANIVERSARIO: { main: '#12B76A', light: '#ECFDF3', dark: '#027A48' },
};

export const TIPO_LABEL = {
  NACIONAL: 'Nacional',
  ESTADUAL: 'Estadual',
  MUNICIPAL: 'Municipal',
  DAY_OFF_ANIVERSARIO: 'Day off aniversário',
};

/** sx pronto para um Chip MUI com fundo claro + texto escuro na cor do tipo (ou cinza p/ inválido/desconhecido). */
export const tipoChipSx = (tipo) => {
  const cor = TIPO_COLOR[tipo];
  if (!cor) return {};
  return {
    bgcolor: cor.light,
    color: cor.dark,
    fontWeight: 700,
  };
};

export const LEGEND_TIPO_KEYS = Object.keys(TIPO_LABEL);

export const LEGEND_FOCUS_CANCELADOS = 'CANCELADOS';

/** Estado inicial da legenda spotlight: sem foco = mostra todos. */
export const createDefaultLegendFilter = () => ({
  focus: null,
});

/**
 * Filtra feriados pela legenda spotlight.
 * focus null → todos; CANCELADOS → só INVALIDO; tipo → só VALIDO daquele tipo.
 */
export const filterFeriadosByLegend = (feriados, legendFilter) => {
  if (!legendFilter) return feriados || [];
  const { focus } = legendFilter;
  if (focus == null) return feriados || [];
  if (focus === LEGEND_FOCUS_CANCELADOS) {
    return (feriados || []).filter((f) => (f.status || 'VALIDO') === 'INVALIDO');
  }
  return (feriados || []).filter(
    (f) => (f.status || 'VALIDO') === 'VALIDO' && f.tipo === focus
  );
};

/**
 * Conta dias úteis (seg–sex) no mês de `referenceDate`, excluindo feriados VALIDO
 * que caem em dia de semana nesse mês. Day-off e demais tipos VALIDO entram na exclusão.
 */
export const countDiasUteisNoMes = (referenceDate, feriados) => {
  const ref = moment(referenceDate);
  const start = ref.clone().startOf('month');
  const end = ref.clone().endOf('month');
  const counted = countDiasUteisNoIntervalo(start, end, feriados);
  return { diasUteis: counted.diasUteis, feriadosNoMes: counted.feriadosNoIntervalo };
};

/**
 * Seg–sex em [inicio, fim] (inclusive), excluindo feriados VALIDO em dia útil.
 * `tipos` filtra o tipo; default = todos os VALIDO (paridade com countDiasUteisNoMes).
 */
export const countDiasUteisNoIntervalo = (inicio, fim, feriados, tipos = null) => {
  const start = moment(inicio).startOf('day');
  const end = moment(fim).startOf('day');
  if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) {
    return { diasUteis: 0, feriadosNoIntervalo: 0 };
  }
  const tipoSet = Array.isArray(tipos) && tipos.length > 0 ? new Set(tipos) : null;
  const holidayKeys = new Set();
  (feriados || []).forEach((f) => {
    if ((f.status || 'VALIDO') !== 'VALIDO') return;
    if (tipoSet && !tipoSet.has(f.tipo)) return;
    const d = moment(f.data);
    if (d.isBefore(start, 'day') || d.isAfter(end, 'day')) return;
    if (d.day() === 0 || d.day() === 6) return;
    holidayKeys.add(d.format('YYYY-MM-DD'));
  });
  let uteis = 0;
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, 'day')) {
    const dow = cursor.day();
    if (dow !== 0 && dow !== 6 && !holidayKeys.has(cursor.format('YYYY-MM-DD'))) {
      uteis += 1;
    }
    cursor.add(1, 'day');
  }
  return { diasUteis: uteis, feriadosNoIntervalo: holidayKeys.size };
};
